/**
 * DATA.REACH guarantee (D2) — Opfibration structure.
 *
 * Neutralizes epistemic lift (G2) by constraining where data can flow.
 * Every data movement must follow declared reach policies.
 *
 * Agda source: Kernel/DATA/REACH/Neutralization.agda
 * Realization fidelity: STRUCTURAL (reach policies enforced; full opfibration laws documented)
 * Safety class: Compositional (innate immunity)
 * Neutralization mode: IMMUNE — violations have no type representation
 * Tier: 2 (structural-geometric)
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type { Brand, Guarantee } from "../foundation/types.js";
import { DATA_REACH_META } from "../shared/theorem-meta.js";
export { DATA_REACH_META };

// ── Structure: Opfibration ─────────────────────────────────────

/** A classification label controlling where data can reach. */
export type Classification<C extends string> = Brand<C, "Classification">;

/** Data with a classification controlling its flow. */
export interface ClassifiedData<T, C extends string> {
  readonly value: T;
  readonly classification: Classification<C>;
}

/** A reach policy — which classifications can flow to which destinations. */
export interface ReachPolicy<C extends string, D extends string> {
  readonly classification: C;
  readonly allowedDestinations: readonly D[];
}

/** Classify data for reach control. */
export function classify<T, C extends string>(value: T, classification: C): ClassifiedData<T, C> {
  return { value, classification: classification as Classification<C> };
}

export interface DataReachViolation {
  readonly __brand: "DataReachViolation";
  readonly kind: "unauthorized-flow";
}

export interface DataReachStructure<C extends string, D extends string> {
  readonly policies: readonly ReachPolicy<C, D>[];
  readonly checkReach: (classification: C, destination: D) => boolean;
}

export function createDataReachGuarantee<C extends string, D extends string>(
  structure: DataReachStructure<C, D>,
): Guarantee<DataReachStructure<C, D>, DataReachViolation> {
  return {
    structure,
    neutralize: (_violation: DataReachViolation): never => {
      throw new Error(
        "DATA.REACH violation is structurally inexpressible: " +
          "classification brands control all data flow",
      );
    },
    meta: DATA_REACH_META,
  };
}
